import {
	Alert,
	AlertIcon,
	Box,
	Button,
	FormControl,
	FormLabel,
	HStack,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Select,
	Text,
	Textarea,
	VStack
} from '@chakra-ui/react';
import { useRef, useState, type JSX } from 'react';
import { useKeystoneSession } from '../../auth/use-keystone-session';

import { Controller } from '@context';
import { buildDiagnosticsBundle } from '@features/customer/helpers';
import { formatBugReference } from '@helpers';
import { lang } from '@lang';
import { selectCustomerName, selectNavigation, useAppSelector } from '@state';
import { BUG_TRACKER_AUTH_EXPIRED, saveCustomerBlueprint, submitBugReport, type BugReportResponse, type BugReportScreenshot } from '@external';
import { useDesign } from '@hooks';

const URGENCY_OPTIONS = ['Low/Question', 'Normal', 'Time sensitive', 'Blocking my work'];
const REQUEST_TYPE_OPTIONS = ['Bug', 'Feature Request'];
const SYSTEM_OPTIONS = ['BP Tool'];
const MAX_SCREENSHOT_BYTES = 4 * 1024 * 1024;
const MAX_SCREENSHOTS = 5;

type ReportBugFormProps = {
	description: string;
	urgency: string;
	system: string;
	requestType: string;
	otherLinks: string;
	screenshots: Array<BugReportScreenshot>;
	submitError: string | null;
	isSubmitting: boolean;
	canSubmit: boolean;
	onDescriptionChange: (value: string) => void;
	onUrgencyChange: (value: string) => void;
	onSystemChange: (value: string) => void;
	onRequestTypeChange: (value: string) => void;
	onOtherLinksChange: (value: string) => void;
	onAddFiles: (files: FileList | null) => void;
	onRemoveScreenshot: (index: number) => void;
	onCancel: () => void;
	onSubmit: () => void;
};

const ReportBugForm = (props: ReportBugFormProps): JSX.Element => {
	const {
		description,
		urgency,
		system,
		requestType,
		otherLinks,
		screenshots,
		submitError,
		isSubmitting,
		canSubmit,
		onDescriptionChange,
		onUrgencyChange,
		onSystemChange,
		onRequestTypeChange,
		onOtherLinksChange,
		onAddFiles,
		onRemoveScreenshot,
		onCancel,
		onSubmit
	} = props;

	const fileInputRef = useRef<HTMLInputElement>(null);

	return (
		<>
			<ModalHeader>{lang.customer.label.reportBug}</ModalHeader>
			<ModalBody>
				<VStack align="stretch" spacing={4}>
					{submitError && (
						<Alert status="error" borderRadius="md">
							<AlertIcon />
							{submitError}
						</Alert>
					)}
					<HStack align="start" spacing={3}>
						<FormControl isRequired>
							<FormLabel>Request type</FormLabel>
							<Select value={requestType} onChange={(e) => onRequestTypeChange(e.target.value)}>
								{REQUEST_TYPE_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
							</Select>
						</FormControl>
						<FormControl isRequired>
							<FormLabel>How urgent is this?</FormLabel>
							<Select value={urgency} onChange={(e) => onUrgencyChange(e.target.value)}>
								{URGENCY_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
							</Select>
						</FormControl>
						<FormControl isRequired>
							<FormLabel>System</FormLabel>
							<Select value={system} onChange={(e) => onSystemChange(e.target.value)}>
								{SYSTEM_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
							</Select>
						</FormControl>
					</HStack>
					<FormControl isRequired>
						<FormLabel>{lang.customer.label.reportBugDescription}</FormLabel>
						<Textarea
							value={description}
							onChange={(e) => onDescriptionChange(e.target.value)}
							placeholder={lang.customer.text.reportBugDescriptionPlaceholder}
							rows={6}
						/>
					</FormControl>
					<FormControl>
						<FormLabel>Other relevant links</FormLabel>
						<Input
							value={otherLinks}
							onChange={(e) => onOtherLinksChange(e.target.value)}
							placeholder="HubSpot record, related tickets, etc."
						/>
					</FormControl>
					<FormControl>
						<FormLabel>Screenshots</FormLabel>
						<Box
							borderWidth="1px"
							borderStyle="dashed"
							borderRadius="md"
							p={4}
							textAlign="center"
							cursor="pointer"
							onClick={() => fileInputRef.current?.click()}
							onDragOver={(e) => e.preventDefault()}
							onDrop={(e) => { e.preventDefault(); onAddFiles(e.dataTransfer.files); }}
						>
							<Text fontSize="sm" color="gray.500">Drop images here, or click to browse</Text>
							<Input
								ref={fileInputRef}
								type="file"
								accept="image/*"
								multiple
								display="none"
								onChange={(e) => { onAddFiles(e.target.files); e.target.value = ''; }}
							/>
						</Box>
						{screenshots.length > 0 && (
							<VStack align="stretch" spacing={1} mt={2}>
								{screenshots.map((shot, index) => (
									<HStack key={`${shot.fileName}-${index}`} justify="space-between">
										<Text fontSize="xs" noOfLines={1}>{shot.fileName}</Text>
										<Button size="xs" variant="ghost" onClick={() => onRemoveScreenshot(index)} isDisabled={isSubmitting}>Remove</Button>
									</HStack>
								))}
							</VStack>
						)}
					</FormControl>
				</VStack>
			</ModalBody>
			<ModalFooter>
				<Button variant="ghost" mr={3} onClick={onCancel} isDisabled={isSubmitting}>Cancel</Button>
				<Button colorScheme="blue" onClick={onSubmit} isDisabled={!canSubmit} isLoading={isSubmitting}>
					{lang.customer.label.reportBugSubmit}
				</Button>
			</ModalFooter>
		</>
	);
};

type PushDesignToTestParams = {
	controller: Controller;
	customerEmail: string;
	getToken: () => Promise<string>;
};

/**
 * Pushes the reported design to TEST through the very same call the "Push To Test" button makes,
 * so triage opens the real design instead of reproducing it. Best-effort: saveCustomerBlueprint
 * returns a tuple rather than throwing, and a failed push must never fail the bug report.
 */
const pushDesignToTest = async (params: PushDesignToTestParams): Promise<void> => {
	const { controller, customerEmail, getToken } = params;
	const [, pushError] = await saveCustomerBlueprint({ controller, customerEmail, mode: 'test', getToken });
	if (pushError) console.warn('[Report a Bug] design push to test failed', pushError);
};

type ReportBugSuccessProps = {
	result: BugReportResponse;
	onDone: () => void;
};

const ReportBugSuccess = (props: ReportBugSuccessProps): JSX.Element => {
	const { result, onDone } = props;

	return (
		<>
			<ModalHeader>{lang.customer.label.reportBugSuccessTitle}</ModalHeader>
			<ModalBody>
				<VStack align="stretch" spacing={2}>
					<Alert status="success" borderRadius="md">
						<AlertIcon />
						{lang.customer.message.reportBugSuccess}
					</Alert>
					<Text fontSize="sm" color="gray.700" fontWeight="600">Reference: {formatBugReference(result.id)}</Text>
					<Text fontSize="xs" color="gray.500">ID: {result.id}</Text>
					<Text fontSize="sm" color="gray.600">Status: {result.status}</Text>
				</VStack>
			</ModalBody>
			<ModalFooter>
				<Button colorScheme="blue" onClick={onDone}>Done</Button>
			</ModalFooter>
		</>
	);
};

export type ReportBugModalProps = {
	isOpen: boolean;
	onClose: () => void;
};

export const ReportBugModal = (props: ReportBugModalProps): JSX.Element => {
	const { isOpen, onClose } = props;

	const [controller] = useDesign();
	const { user, getToken } = useKeystoneSession();
	const customerName = useAppSelector(selectCustomerName);
	const appNav = useAppSelector(selectNavigation);
	const blueprintId = controller?.kitchen?.context?.blueprint?.id ?? '';

	const [description, setDescription] = useState<string>('');
	const [urgency, setUrgency] = useState<string>('Normal');
	const [system, setSystem] = useState<string>('BP Tool');
	const [requestType, setRequestType] = useState<string>('Bug');
	const [otherLinks, setOtherLinks] = useState<string>('');
	const [screenshots, setScreenshots] = useState<Array<BugReportScreenshot>>([]);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [result, setResult] = useState<BugReportResponse | null>(null);

	const resetAndClose = (): void => {
		setDescription('');
		setUrgency('Normal');
		setSystem('BP Tool');
		setRequestType('Bug');
		setOtherLinks('');
		setScreenshots([]);
		setIsSubmitting(false);
		setSubmitError(null);
		setResult(null);
		onClose();
	};

	const readFileAsBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve((reader.result as string).split(',')[1] ?? '');
		reader.onerror = () => reject(reader.error);
		reader.readAsDataURL(file);
	});

	const handleAddFiles = async (files: FileList | null): Promise<void> => {
		if (!files?.length) return;
		const additions: Array<BugReportScreenshot> = [];
		for (const file of Array.from(files)) {
			if (!file.type.startsWith('image/')) continue;
			if (file.size > MAX_SCREENSHOT_BYTES) {
				setSubmitError(`${file.name} is larger than 4MB and was skipped.`);
				continue;
			}
			additions.push({ fileName: file.name, contentType: file.type, dataBase64: await readFileAsBase64(file) });
		}
		setScreenshots((prev) => [...prev, ...additions].slice(0, MAX_SCREENSHOTS));
	};

	const handleRemoveScreenshot = (index: number): void => {
		setScreenshots((prev) => prev.filter((_shot, i) => i !== index));
	};

	const handleSubmit = async (): Promise<void> => {
		setIsSubmitting(true);
		setSubmitError(null);
		try {
			const bundle = buildDiagnosticsBundle({ controller, userEmail: user?.email ?? null, customerName, blueprintId });
			const [response, error] = await submitBugReport({
				description,
				urgency,
				system,
				requestType,
				otherLinks: otherLinks.trim() || null,
				screenshots,
				url: window.location.href,
				userAgent: navigator.userAgent,
				meta: bundle.meta,
				logs: bundle.logs,
				blueprint: bundle.blueprint,
				reporterRoles: user?.appRoles ?? null,
				getToken
			});

			if (error || !response) {
				setSubmitError(error === BUG_TRACKER_AUTH_EXPIRED ? lang.customer.message.reportBugSessionExpired : (error ?? lang.customer.message.reportBugFailed));
				setIsSubmitting(false);
				return;
			}

			if (controller && appNav.email) await pushDesignToTest({ controller, customerEmail: appNav.email, getToken });

			setResult(response);
			setIsSubmitting(false);
		} catch (err) {
			console.error('[Report a Bug] failed:', err);
			setSubmitError(lang.customer.message.reportBugFailed);
			setIsSubmitting(false);
		}
	};

	const canSubmit = description.trim().length > 0 && !isSubmitting;

	return (
		<Modal isOpen={isOpen} onClose={resetAndClose}>
			<ModalOverlay />
			<ModalContent>
				<ModalCloseButton />
				{result ? (
					<ReportBugSuccess result={result} onDone={resetAndClose} />
				) : (
					<ReportBugForm
						description={description}
						urgency={urgency}
						system={system}
						requestType={requestType}
						otherLinks={otherLinks}
						screenshots={screenshots}
						submitError={submitError}
						isSubmitting={isSubmitting}
						canSubmit={canSubmit}
						onDescriptionChange={setDescription}
						onUrgencyChange={setUrgency}
						onSystemChange={setSystem}
						onRequestTypeChange={setRequestType}
						onOtherLinksChange={setOtherLinks}
						onAddFiles={(files) => { handleAddFiles(files); }}
						onRemoveScreenshot={handleRemoveScreenshot}
						onCancel={resetAndClose}
						onSubmit={() => { handleSubmit(); }}
					/>
				)}
			</ModalContent>
		</Modal>
	);
};
